/**
 * SiteDetailPanel — E-2. 현장 상세
 *
 * fixed overlay 팝업. SiteArea에서 연필 버튼 클릭 시 표시.
 * - 조회 모드: 현장·고객 정보 읽기전용
 * - 편집 모드: PATCH /sites/{siteId} (customerName·mobile 포함 단일 호출)
 * - [현장 종료] 토글(상태 배지 오른쪽): 켜기 = PATCH /sites/{siteId}/status { DONE }, 끄기 = PATCH /sites/{siteId}/status/reopen
 *   — 둘 다 확인창 뒤 실행. 편집 모드에서는 숨김. 종료(거래완료) 현장은 편집 불가(해제 후 편집)
 * - 현장 담당·관리(2026-10-12 개선사항 D-2): 보기 = 이름 쉼표 나열, 편집 = MultiSelect(담당자 1명 이상)
 */
import { useState, useEffect, useId } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Badge, Skeleton, Textarea } from '@indipia/ui-core';
import {
  SiteMapPickerPopup, AssigneeSelect, useCompanyMembers,
  BellIcon, ChargeNotiMark, NotiStateMark,
} from '@indipia/shared-features';
import { useAuthStore } from '@indipia/shared';
import {
  fetchSiteDetail,
  fetchFloorPlanSizeType,
  updateSiteInfo,
  updateSiteStatusDone,
  reopenSite,
} from '../../shared/api/sites';
import { ConfirmDialog } from '../../shared/components/ConfirmDialog';

// ── 헬퍼 ────────────────────────────────────────────────────────────────────

// 현장 상태 → ui-core Badge status 매핑 (DQ-C03)
function siteBadgeStatus(status: string): 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'ACTIVE':   return 'info';
    case 'PROGRESS': return 'warning';
    case 'DONE':     return 'success';
    default:         return 'error';
  }
}

function planStatusLabel(status: string): string {
  if (status === 'READY')      return '도면 완료';
  if (status === 'CONVERTING') return '변환 중';
  return '도면 없음';
}

function planStatusCls(status: string): string {
  if (status === 'READY')      return 'text-success';
  if (status === 'CONVERTING') return 'text-warning';
  return 'text-text-muted';
}

// ── 하위 컴포넌트 ────────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 mb-2 text-xs font-bold uppercase tracking-widest text-text-muted first:mt-0">
      {children}
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2.5 border-b border-border py-1.5 last:border-b-0">
      {/* 고정 라벨 너비·행값 폰트: DQ-T04/T03 대응 표준 토큰 없음 — 의도 유지 */}
      <span className="min-w-[54px] shrink-0 pt-px text-caption text-text-secondary">{label}</span>
      <span className="flex-1 text-caption font-medium text-text">{children}</span>
    </div>
  );
}

// DQ-C03 예외: ui-core Input(.input)은 48px 폼필드(max-w 320) 규격 →
// 데이터 행의 compact 인라인 편집과 부적합하여 원시 input 유지.
// 행값 폰트도 DQ-T03 대응 표준 토큰 없어 유지.
function EditInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xs border border-border bg-surface px-1.5 py-px text-caption font-medium text-text focus:border-point focus:outline-none"
    />
  );
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────

interface SiteDetailPanelProps {
  siteId: number;
  onClose: () => void;
}

interface EditForm {
  customerName: string;
  mobile: string;
  addressDtl: string;
  bldgName: string;
  dong: string;
  ho: string;
  memo: string;
  gpsLat: number | null;
  gpsLng: number | null;
  chargeIds: number[];
  managerIds: number[];
  /** 알림받기를 켠 관리자(20261015) — 편집에서 새로 추가한 관리자는 꺼진 상태로 시작 */
  managerNotiIds: number[];
  /** 편집에서 새로 추가한 관리자 중 알림 켬/끔을 직접 고른 사람 — 관리자 지정 시 알림 수신 여부는 필수(확정 결정 6) */
  managerNotiChosenIds: number[];
}

function sameIds(a: number[], b: number[]) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** 알림받기(NOTI_RECEIVE_YN='Y')인 사람의 회원ID */
function notiOnIds(list?: { memberId: number; notiReceiveYn?: string | null }[] | null) {
  return (list ?? []).filter((m) => m.notiReceiveYn === 'Y').map((m) => m.memberId);
}

export function SiteDetailPanel({ siteId, onClose }: SiteDetailPanelProps) {
  const queryClient = useQueryClient();
  const [isEdit, setIsEdit]   = useState(false);
  const [form,   setForm]     = useState<EditForm | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  /** 현장 종료 토글 확인창 — 'end' 종료(거래완료) · 'reopen' 종료 해제 */
  const [endConfirm, setEndConfirm] = useState<'end' | 'reopen' | null>(null);
  const titleId = useId();
  const myId = useAuthStore((s) => s.user?.memberId ?? null);
  const { data: companyMembers = [] } = useCompanyMembers();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // 확인창이 떠 있으면 Esc 는 확인창만 닫는다(팝업까지 닫지 않음)
      if (e.key === 'Escape' && !endConfirm) onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, endConfirm]);

  const { data: site, isLoading } = useQuery({
    queryKey: ['site-detail', siteId],
    queryFn:  () => fetchSiteDetail(siteId),
    staleTime: 30_000,
  });

  const { data: sizeType } = useQuery({
    queryKey: ['floor-plan-size-type', site?.floorPlanId],
    queryFn:  () => fetchFloorPlanSizeType(site!.floorPlanId!),
    enabled:  !!site?.floorPlanId,
    staleTime: 5 * 60_000,
  });

  // 편집 모드 진입 시 폼 초기화
  useEffect(() => {
    if (isEdit && site) {
      setForm({
        customerName: site.customerName ?? '',
        mobile:       site.customerMobile ?? '',
        addressDtl:   site.addressDtl ?? '',
        bldgName:     site.bldgName ?? '',
        dong:         site.addressDong ?? '',
        ho:           site.addressHo ?? '',
        memo:         site.siteMemo ?? '',
        gpsLat:       site.gpsLat ?? null,
        gpsLng:       site.gpsLng ?? null,
        chargeIds:    (site.chargeMembers ?? []).map((m) => m.memberId),
        managerIds:   (site.managerMembers ?? []).map((m) => m.memberId),
        managerNotiIds: notiOnIds(site.managerMembers),
        managerNotiChosenIds: [],
      });
    }
  }, [isEdit, site]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['site-detail', siteId] });
    queryClient.invalidateQueries({ queryKey: ['site-list'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (f: EditForm) => {
      await updateSiteInfo(siteId, {
        customerName:   f.customerName || undefined,
        customerMobile: f.mobile ? f.mobile.replace(/-/g, '') : undefined,
        addressDtl:     f.addressDtl || undefined,
        bldgName:       f.bldgName   || undefined,
        addressDong:    f.dong       || undefined,
        addressHo:      f.ho         || undefined,
        siteMemo:       f.memo       || undefined,
        gpsLat:         f.gpsLat ?? undefined,
        gpsLng:         f.gpsLng ?? undefined,
        // 담당·관리는 바뀐 역할만 보낸다(배열 = 전체 교체, 미전송 = 변경 없음)
        ...(!sameIds(f.chargeIds, (site?.chargeMembers ?? []).map((m) => m.memberId)) ? { chargeMemberIds: f.chargeIds } : {}),
        ...(!sameIds(f.managerIds, (site?.managerMembers ?? []).map((m) => m.memberId)) ? { managerMemberIds: f.managerIds } : {}),
        // 관리자 알림받기 — 명단이나 켬/끔이 바뀌었을 때만 보낸다(지금 관리자 중 켠 사람만)
        ...(managerNotiChanged(f) ? { managerNotiMemberIds: f.managerIds.filter((id) => f.managerNotiIds.includes(id)) } : {}),
      });
    },
    onSuccess: () => {
      toast.success('현장 정보가 수정되었습니다.');
      setIsEdit(false);
      invalidate();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { code?: string; message?: string } } })?.response?.data;
      // 방어용 — 화면 검증을 통과했는데 서버가 관리자 알림 수신 여부 누락으로 거부한 경우(14-Customer SITE_MANAGER_NOTI_REQUIRED)
      if (data?.code === 'SITE_MANAGER_NOTI_REQUIRED') {
        toast.error('관리자 알림 받기를 켬 또는 끔으로 선택해주세요.');
        return;
      }
      toast.error(data?.message ?? '저장에 실패했습니다.');
    },
  });

  // 현장 종료 토글 — 종료 = 거래완료(DONE), 해제 = 종료 직전 상태로 되돌림
  const endMutation = useMutation({
    mutationFn: (action: 'end' | 'reopen') => (action === 'end' ? updateSiteStatusDone(siteId) : reopenSite(siteId)),
    onSuccess: (_d, action) => {
      toast.success(action === 'end' ? '현장이 종료되었습니다.' : '현장 종료가 해제되었습니다.');
      setEndConfirm(null);
      invalidate();
    },
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message ?? '상태 변경에 실패했습니다.');
      setEndConfirm(null);
    },
  });


  // 문자열 입력 필드만 대상 (좌표 gpsLat/gpsLng는 지도 팝업으로 별도 설정)
  type StringFieldKey = 'customerName' | 'mobile' | 'addressDtl' | 'bldgName' | 'dong' | 'ho' | 'memo';
  function field(key: StringFieldKey) {
    return form?.[key] ?? '';
  }
  function setField(key: StringFieldKey) {
    return (v: string) => setForm((prev) => prev ? { ...prev, [key]: v } : prev);
  }

  function handleSave() {
    if (!form) return;
    if (form.chargeIds.length === 0) return;   // 현장 담당자 1명 이상 필수 — 오류 표시는 필드에서
    if (managerNotiUndecided(form).length > 0) return;   // 새 관리자의 알림 수신 여부 필수 — 오류 표시는 필드에서
    saveMutation.mutate(form);
  }

  function handleEditToggle() {
    if (isEdit) {
      handleSave();
    } else {
      setIsEdit(true);
    }
  }

  // [취소] — 폼을 버리면 다음 편집 때 site 값으로 다시 채워져 원래 값으로 복원된다
  function handleCancel() {
    setIsEdit(false);
    setForm(null);
  }

  const chargeEmpty = isEdit && !!form && form.chargeIds.length === 0;

  // 알림 수신 여부를 아직 고르지 않은 새 관리자 — 기존 관리자는 저장된 켬/끔 값이 있어 해당 없음
  function managerNotiUndecided(f: EditForm): number[] {
    const existing = new Set((site?.managerMembers ?? []).map((m) => m.memberId));
    return f.managerIds.filter((id) => !existing.has(id) && !f.managerNotiChosenIds.includes(id));
  }
  const managerNotiEmpty = isEdit && !!form && managerNotiUndecided(form).length > 0;

  // 관리자 알림받기 변경 여부 — 명단이 바뀌었거나, 남은 관리자 중 켬/끔이 바뀐 경우
  function managerNotiChanged(f: EditForm): boolean {
    const before = (site?.managerMembers ?? []).map((m) => m.memberId);
    if (!sameIds(f.managerIds, before)) return true;
    const beforeOn = new Set(notiOnIds(site?.managerMembers));
    return f.managerIds.some((id) => f.managerNotiIds.includes(id) !== beforeOn.has(id));
  }

  // 해제·탈퇴해 구성원 목록에 없는 기존 지정자도 칩에 이름이 보이게
  const assignedNames: Record<number, string | null> = Object.fromEntries(
    [...(site?.chargeMembers ?? []), ...(site?.managerMembers ?? [])].map((m) => [m.memberId, m.memberName]),
  );

  // 헤더 서브타이틀 — 평형/타입 우선, 주소는 뒤로 (주소가 길어도 평형 잘림 방지)
  function buildSubtitle(): string {
    if (!site) return '';
    const parts: string[] = [];
    // 평형·타입: 둘 다 있으면 "17A", 평만 있으면 "17평", 타입만 있으면 "A형"
    if (site.sitePyeong && sizeType) parts.push(`${site.sitePyeong}${sizeType}`);
    else if (site.sitePyeong)        parts.push(`${site.sitePyeong}평`);
    else if (sizeType)           parts.push(`${sizeType}형`);
    const loc: string[] = [];
    if (site.addressDong) loc.push(`${site.addressDong}동`);
    if (site.addressHo)   loc.push(`${site.addressHo}호`);
    if (loc.length) parts.push(loc.join(' '));
    if (site.siteAddress) parts.push(site.siteAddress);
    return parts.join(' · ');
  }

  const isDone = site?.siteStatus === 'DONE';

  // DQ-C03 예외: ui-core Modal(.modal-box)은 420px·radius24 고정 규격이라
  // 커스텀 460px 헤더/스크롤/푸터 레이아웃과 상이 → 커스텀 오버레이 유지(ESC·외부클릭 로직 포함).
  // 팝업 사이즈 정합은 추후 개별 수정 예정. 모달 폭도 DQ-T04 대응 토큰 없어 유지.
  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-6"
      style={{ background: 'var(--overlay-scrim)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex w-full max-w-[460px] flex-col overflow-hidden rounded-xl bg-surface-raised shadow-xl"
        style={{ maxHeight: 'calc(100vh - 48px)' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >

        {/* ── 헤더 ── */}
        <div className="flex shrink-0 items-start justify-between border-b border-border px-5 pb-3 pt-4">
          {isLoading ? (
            <Skeleton width={192} height={40} />
          ) : (
            <div className="min-w-0 flex-1 pr-3">
              {/* 제목/서브타이틀 폰트: DQ-T03 대응 표준 토큰 없음 — 의도 유지 */}
              <div id={titleId} className="truncate text-[17px] font-bold tracking-tight text-text">
                {site?.customerName}{site?.bldgName ? ` · ${site.bldgName}` : ''}
              </div>
              <div className="mt-0.5 truncate text-caption text-text-secondary">
                {buildSubtitle()}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-title-sm leading-none text-text-muted hover:text-text"
          >
            ×
          </button>
        </div>

        {/* ── 바디 ── */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height={28} />
              ))}
            </div>
          ) : site ? (
            <>
              {/* 상태 뱃지 — ui-core Badge (DQ-C03) + 오른쪽에 현장 종료 토글(편집 중에는 숨김) */}
              <div className="mb-4 flex items-center gap-2">
                <Badge status={siteBadgeStatus(site.siteStatus)}>
                  {site.siteStatusName ?? site.siteStatus}
                </Badge>
                {!isEdit && (
                  // 오른쪽 끝 · 테두리 없음 — 켜짐(종료됨)만 danger 채움(경고 표시 전용, D-09 btn-danger), 꺼짐은 ghost
                  <Button
                    type="button"
                    size="sm"
                    variant={isDone ? 'danger' : 'ghost'}
                    aria-pressed={isDone}
                    loading={endMutation.isPending}
                    onClick={() => setEndConfirm(isDone ? 'reopen' : 'end')}
                    className="ml-auto"
                  >
                    현장 종료
                  </Button>
                )}
              </div>

              {/* 고객 정보 */}
              <SectionTitle>고객 정보</SectionTitle>
              <Row label="고객명">
                {isEdit
                  ? <EditInput value={field('customerName')} onChange={setField('customerName')} />
                  : site.customerName}
              </Row>
              <Row label="연락처">
                {isEdit
                  ? <EditInput value={field('mobile')} onChange={setField('mobile')} type="tel" />
                  : site.customerMobile}
              </Row>

              {/* 현장 정보 */}
              <SectionTitle>현장 정보</SectionTitle>
              <Row label="도로명">
                <span className="text-text-muted">{site.siteAddress}</span>
              </Row>
              <Row label="상세주소">
                {isEdit
                  ? <EditInput value={field('addressDtl')} onChange={setField('addressDtl')} placeholder="상세주소" />
                  : (site.addressDtl || <span className="text-text-muted">—</span>)}
              </Row>
              <Row label="단지명">
                {isEdit
                  ? <EditInput value={field('bldgName')} onChange={setField('bldgName')} placeholder="단지명" />
                  : (site.bldgName || <span className="text-text-muted">—</span>)}
              </Row>
              <Row label="동/호">
                {isEdit ? (
                  <div className="flex gap-1.5">
                    <EditInput value={field('dong')} onChange={setField('dong')} placeholder="동" />
                    <EditInput value={field('ho')} onChange={setField('ho')} placeholder="호" />
                  </div>
                ) : (
                  [site.addressDong && `${site.addressDong}동`, site.addressHo && `${site.addressHo}호`].filter(Boolean).join(' ') || <span className="text-text-muted">—</span>
                )}
              </Row>
              {/* 위치(지도) — 편집 모드에서만 지도로 좌표 지정 */}
              {isEdit && (
                <Row label="위치">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-caption text-text-secondary">
                      {form?.gpsLat != null && form?.gpsLng != null
                        ? `${form.gpsLat.toFixed(6)}, ${form.gpsLng.toFixed(6)}`
                        : '미지정'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setMapOpen(true)}
                      className="rounded-sm border border-border px-2 py-0.5 text-caption font-semibold text-point hover:bg-surface"
                    >
                      지도에서 지정
                    </button>
                  </div>
                </Row>
              )}
              <Row label="도면">
                <span className={`text-body-sm font-semibold ${planStatusCls(site.planStatus)}`}>
                  {planStatusLabel(site.planStatus)}
                </span>
              </Row>

              {/* 현장 담당·관리 — 2026-10-12 개선사항 D-2 */}
              <SectionTitle>현장 담당·관리</SectionTitle>
              {isEdit && form ? (
                // 편집 모드도 다른 편집 행과 같은 "왼쪽 라벨 · 오른쪽 입력" 행 구성(기획 E-0)
                <>
                  <Row label="담당자">
                    {/* 담당자는 알림 항상 받음 — 끌 수 없어 칩 토글 없이 일반 선택 + 안내 문구 */}
                    <AssigneeSelect
                      members={companyMembers}
                      values={form.chargeIds}
                      onChange={(v) => setForm((prev) => (prev ? { ...prev, chargeIds: v } : prev))}
                      myId={myId}
                      oppositeIds={form.managerIds}
                      oppositeLabel="관리자"
                      extraNames={assignedNames}
                      error={chargeEmpty ? '담당자를 1명 이상 선택해주세요.' : undefined}
                    />
                    <span className="mt-02 block text-caption text-text-muted">담당자는 알림을 항상 받습니다.</span>
                  </Row>
                  <Row label="관리자">
                    {/* 관리자 칩의 🔔/🔕 — 누르면 그 사람만 켜고 끈다. 새로 추가한 관리자는 꺼진 채로 시작, [저장] 시 반영 */}
                    <AssigneeSelect
                      members={companyMembers}
                      values={form.managerIds}
                      onChange={(v) => setForm((prev) => (prev ? { ...prev, managerIds: v } : prev))}
                      notiIds={form.managerNotiIds}
                      onNotiChange={(ids) => setForm((prev) => {
                        if (!prev) return prev;
                        // 이번에 켜거나 끈 사람을 '직접 고른 사람'으로 기록
                        const changed = [...ids.filter((id) => !prev.managerNotiIds.includes(id)), ...prev.managerNotiIds.filter((id) => !ids.includes(id))];
                        return { ...prev, managerNotiIds: ids, managerNotiChosenIds: [...new Set([...prev.managerNotiChosenIds, ...changed])] };
                      })}
                      myId={myId}
                      oppositeIds={form.chargeIds}
                      oppositeLabel="담당자"
                      extraNames={assignedNames}
                      error={managerNotiEmpty ? '새로 지정한 관리자의 알림 받기를 켜거나 꺼서 선택해주세요.' : undefined}
                    />
                    {form.managerIds.length > 0 && (
                      <span className="mt-02 flex items-center gap-01 text-caption text-text-muted">
                        <BellIcon size={12} className="text-group" />를 눌러 알림을 켜고 끌 수 있어요.
                      </span>
                    )}
                  </Row>
                </>
              ) : (
                <>
                  <Row label="담당자">
                    {site.chargeMembers?.length ? (
                      <span className="flex flex-wrap items-center gap-x-04 gap-y-01">
                        {site.chargeMembers.map((m) => (
                          <span key={m.memberId} className="inline-flex items-center gap-01">
                            {m.memberName ?? '(이름 없음)'}<ChargeNotiMark />
                          </span>
                        ))}
                      </span>
                    ) : <span className="text-text-muted">—</span>}
                  </Row>
                  <Row label="관리자">
                    {site.managerMembers?.length ? (
                      <span className="flex flex-wrap items-center gap-x-04 gap-y-01">
                        {site.managerMembers.map((m) => (
                          <span key={m.memberId} className="inline-flex items-center gap-01">
                            {m.memberName ?? '(이름 없음)'}<NotiStateMark on={m.notiReceiveYn === 'Y'} />
                          </span>
                        ))}
                      </span>
                    ) : <span className="text-text-muted">—</span>}
                  </Row>
                </>
              )}

              {/* 메모 */}
              <SectionTitle>메모</SectionTitle>
              {isEdit ? (
                <Textarea
                  className="field-auto"
                  value={field('memo')}
                  onChange={(e) => setField('memo')(e.target.value)}
                  placeholder="메모를 입력하세요"
                  rows={3}
                />
              ) : (
                site.siteMemo
                  ? <div className="rounded-sm bg-surface px-2.5 py-2.5 text-body-sm leading-relaxed text-text-secondary">{site.siteMemo}</div>
                  : <div className="text-body-sm text-text-muted">메모 없음</div>
              )}
            </>
          ) : null}
        </div>

        {isEdit && saveMutation.isError && (
          <p className="shrink-0 border-t border-border px-5 py-2 text-xs text-error">
            저장에 실패했습니다. 다시 시도해주세요.
          </p>
        )}

        {/* ── 푸터 ── */}
        <div className="flex shrink-0 gap-2 border-t border-border px-5 py-3.5">
          {/* 취소 버튼 — 편집 모드에서만 (ui-core Button, DQ-C03) */}
          {isEdit && (
            <Button
              variant="ghost"
              onClick={handleCancel}
              disabled={saveMutation.isPending}
              className="flex-1"
            >
              취소
            </Button>
          )}

          {/* 편집 / 저장 토글 (ui-core Button, DQ-C03) */}
          <Button
            variant={isEdit ? 'primary' : 'secondary'}
            onClick={handleEditToggle}
            disabled={isLoading || isDone || chargeEmpty || managerNotiEmpty}
            loading={saveMutation.isPending}
            className="flex-1"
          >
            {isEdit ? '저장' : '편집'}
          </Button>
        </div>

      </div>

      {/* 현장 종료 토글 확인 */}
      {endConfirm && (
        <ConfirmDialog
          open
          message={endConfirm === 'end' ? '거래완료가 맞나요?' : '현장 종료를 해제할까요?'}
          description={endConfirm === 'end'
            ? '종료된 현장은 기본 목록에서 빠지고 열람 전용이 됩니다. 종료는 다시 해제할 수 있어요.'
            : '종료 직전 상태로 돌아가고 다시 편집할 수 있어요. 현장 종료로 완료됐던 공사 스케줄 · 공사현황도 다시 열려요.'}
          confirmLabel={endConfirm === 'end' ? '현장 종료' : '종료 해제'}
          pendingLabel="처리 중…"
          isPending={endMutation.isPending}
          onConfirm={() => endMutation.mutate(endConfirm)}
          onCancel={() => setEndConfirm(null)}
        />
      )}

      {/* 위치 지정 지도 팝업 (편집 모드) */}
      {mapOpen && site && (
        <SiteMapPickerPopup
          roadAddress={site.siteAddress}
          initialLat={form?.gpsLat ?? site.gpsLat ?? undefined}
          initialLng={form?.gpsLng ?? site.gpsLng ?? undefined}
          onClose={() => setMapOpen(false)}
          onConfirm={(coord) => {
            setForm((prev) => (prev ? { ...prev, gpsLat: coord.lat, gpsLng: coord.lng } : prev));
            setMapOpen(false);
          }}
        />
      )}
    </div>
  );
}
